import * as React from 'react'

import {
  BulkCreateAttendanceShiftShiftsInput,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import { Moment } from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getDateRange } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getDateRange'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'

interface UseSubmitBulkChangeParams {
  year: number
  employeeId: number
  currentMonth: Moment
  shifts: TimesheetPageShiftFragment[]
}

export const useSubmitBulkChange = ({
  year,
  employeeId,
  currentMonth,
  shifts,
}: UseSubmitBulkChangeParams) => {
  const [halfDayShiftCount, setHalfDayShiftCount] = React.useState<number>(0)
  const { bulkDestroyShift, bulkCreateShift } = useShiftActions()

  const shiftCountForAYear = React.useCallback(async () => {
    const { apiClient: ApiClient } = await import('modules/core/lib/mobx-rest')
    const { value } = await asyncAction(
      () =>
        ApiClient().get('/attendance/shift_stats', {
          year: year,
          employee_id: employeeId,
        }),
      {
        successFlash: false,
      }
    )
    setHalfDayShiftCount(value.half_day_worked / 2)
  }, [year, employeeId])

  React.useEffect(() => {
    shiftCountForAYear()
  }, [shiftCountForAYear, shifts])

  const submitBulkCreate = async (shiftsToCreate: TimesheetPageShiftFragment[]) => {
    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const createShifts: BulkCreateAttendanceShiftShiftsInput[] = shiftsToCreate.map((shift) => ({
      clockIn: shift.clockIn,
      clockOut: shift.clockOut,
      date: shift.date,
      day: stringToDate(shift.date, 'backend').date(),
      employeeId: shift.employeeId,
      halfDay: shift.halfDay,
      locationType: AttendanceShiftUtils.locationType(shift),
      observations: shift.observations,
      referenceDate: shift.referenceDate,
      source: 'desktop',
      timeSettingsBreakConfigurationId: shift.timeSettingsBreakConfiguration?.id,
      workable: shift.workable,
    }))

    const { success } = await asyncAction(
      () =>
        bulkCreateShift({
          shifts: createShifts,
        }),
      {
        successMessage: i18n.t('clock_in.bulk_action.create_success_message', {
          count: shiftsToCreate.length / 2,
        }),
        errorFlash: true,
      }
    )
    app.setProgress(null)

    if (success) {
      shiftCountForAYear()
    }
  }

  const submitBulkDestroy = async (shiftsToDestroy: TimesheetPageShiftFragment[]) => {
    const ids = shiftsToDestroy.map((shift) => shift.id)

    const dateRange = getDateRange(currentMonth)
    const { startOn, endOfMonth: endOn } = dateRange

    const payload = {
      ids,
      employeeId,
      startOn,
      endOn,
    }

    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const { success } = await asyncAction(() => bulkDestroyShift(payload), {
      successMessage: i18n.t('clock_in.bulk_action.destroy_success_message', {
        count: shiftsToDestroy.length / 2,
      }),
      errorFlash: true,
    })

    app.setProgress(null)

    if (success) {
      shiftCountForAYear()
    }
  }

  const submitBulkChange = (shiftsToChange: TimesheetPageShiftFragment[]) => {
    if (!shiftsToChange.length) return

    const sortedShifts = shiftsToChange.sort(
      (a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf()
    )

    const isHalfDayShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? !AttendanceShiftUtils.isNew(shift) : false

    const isDefaultShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? AttendanceShiftUtils.isNew(shift) : false

    if (isHalfDayShift(sortedShifts[0])) {
      submitBulkDestroy(sortedShifts.filter(isHalfDayShift))
    } else {
      submitBulkCreate(sortedShifts.filter(isDefaultShift))
    }
  }

  return { submitBulkChange, halfDayShiftCount, setHalfDayShiftCount }
}
